import React, { useRef, useEffect } from 'react';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { LineChart, ScatterChart, EffectScatterChart } from 'echarts/charts';
import { MarkLineComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useIsReducedMotion } from '@atlaskit/motion';
import { colors, fontSizeSmall } from '@atlaskit/theme';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import type { DateRange, BatchSizeChartDataPoint, EChartsEvent } from '../../../common/types';
import {
	CHART_SPACE_LEFT,
	CHART_SPACE_BOTTOM,
} from '../../../common/ui/batch-size-chart/constants';
import { useHoveredDeployment } from '../../../controllers/hovered-item';
import messages from './messages';
import { useTooltipFormatter } from './tooltip';

echarts.use([
	LineChart,
	ScatterChart,
	EffectScatterChart,
	MarkLineComponent,
	TooltipComponent,
	CanvasRenderer,
]);

export type Props = {
	dataPoints: BatchSizeChartDataPoint[];
	median: number | null;
	dateRange: DateRange;
	onEvents?: {
		click: (event: EChartsEvent<BatchSizeChartDataPoint>) => void;
	};
};

const getAxisCommonConfig = () => ({
	nameGap: gridSize * 4,
	nameLocation: 'middle',
	nameTextStyle: {
		color: colors.N400,
		fontSize: fontSizeSmall(),
	},
});

const useXAxisConfig = (dateRange: DateRange) => {
	const { formatDate } = useIntl();

	const xAxisData = eachDayOfInterval({
		start: dateRange.startDateInclusive,
		end: dateRange.endDateExclusive,
	}).map((x) => x.getTime());

	return {
		...getAxisCommonConfig(),
		data: xAxisData,
		min: xAxisData[0],
		max: xAxisData[xAxisData.length - 1],
		splitNumber: xAxisData.length,
		axisLabel: {
			// @ts-expect-error - TS7006 - Parameter 'x' implicitly has an 'any' type.
			formatter: (x) =>
				formatDate(new Date(parseInt(x, 10)), {
					month: 'short',
					day: 'numeric',
				}),
			showMaxLabel: true,
		},
		splitLine: {
			show: false,
		},
		axisLine: {
			lineStyle: {
				color: colors.N40,
				width: 2,
			},
		},
		axisTick: {
			alignWithLabel: true,
		},
		offset: 10,
		scale: true,
		type: 'time',
		axisPointer: {
			show: true,
			lineStyle: {
				type: 'dashed',
				color: colors.B100,
				width: 0.5,
			},
			label: {
				show: false,
			},
			triggerTooltip: false,
		},
	};
};

const Chart = ({ onEvents, dataPoints, median, dateRange }: Props) => {
	const { formatMessage } = useIntl();

	const tooltipFormatter = useTooltipFormatter();
	const xAxisConfig = useXAxisConfig(dateRange);
	const echartsRef = useRef<unknown>();
	const [hoveredDeployment] = useHoveredDeployment();
	const prefersReducedMotion = useIsReducedMotion();

	useEffect(() => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		const echartsInstance = echartsRef?.current?.getEchartsInstance();
		if (echartsInstance === undefined || hoveredDeployment === null) {
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			return () => {};
		}
		echartsInstance.dispatchAction({
			type: 'highlight',
			name: hoveredDeployment,
		});
		return () => {
			echartsInstance.dispatchAction({
				type: 'downplay',
				name: hoveredDeployment,
			});
		};
	}, [hoveredDeployment]);

	// TODO RLSS-2812: handle darkmode for echarts
	const scatterConfig = {
		type: 'effectScatter',
		data: dataPoints,
		showEffectOn: 'emphasis',
		rippleEffect: {
			brushType: 'fill',
			color: colors.B300,
			scale: 5,
		},
		hoverAnimation: true,
		color: colors.B75,
		symbolSize: 8,
		itemStyle: {
			borderColor: colors.B100,
			borderWidth: 1,
		},
		emphasis: {
			itemStyle: {
				color: colors.N0,
				borderColor: colors.B300,
				borderWidth: 2,
				borderType: 'solid',
				opacity: 1,
			},
		},
		lineStyle: {
			color: colors.B200,
		},
	};

	const chartOptions = {
		animation: !prefersReducedMotion,
		tooltip: {
			backgroundColor: colors.N900,
			formatter: tooltipFormatter,
			confine: true,
			transitionDuration: 0,
			enterable: true,
		},
		grid: {
			left: `${CHART_SPACE_LEFT}px`,
			right: '20px',
			bottom: `${CHART_SPACE_BOTTOM}px`,
		},
		xAxis: xAxisConfig,
		yAxis: {
			...getAxisCommonConfig(),
			name: formatMessage(messages.yAxis),
			nameGap: 40,
			nameLocation: 'middle',
			minInterval: 1,
			offset: 0,
			axisLine: {
				show: false,
			},
			axisTick: {
				show: false,
			},
			splitLine: {
				lineStyle: {
					color: colors.N30,
				},
			},
		},
		textStyle: {
			color: colors.N400,
		},
		series: [
			{
				...scatterConfig,
				...(median === null
					? {}
					: {
							markLine: {
								data: [
									{
										yAxis: median,
									},
								],
								silent: true,
								lineStyle: {
									color: colors.N300,
									width: 1,
									opacity: 0.6,
								},
								symbol: 'none',
								label: { show: false },
							},
						}),
			},
		],
	};

	return (
		<ReactEchartsCore
			// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type 'LegacyRef<EChartsReactCore> | undefined'.
			ref={echartsRef}
			echarts={echarts}
			option={chartOptions}
			onEvents={onEvents}
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={{ height: '100%' }}
		/>
	);
};

export default Chart;
